import React, {memo, useEffect, useMemo, useState} from 'react';
import {Modal, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import {useTranslation} from "react-i18next";
import {getPrintQueueState, subscribeToPrintQueue} from '../helpers/PrintQueueMonitor';

const PrintQueueIndicator = () => {
  const { t } = useTranslation();
  const [queueState, setQueueState] = useState(() => getPrintQueueState());
  const [modalVisible, setModalVisible] = useState(false);

  useEffect(() => {
    return subscribeToPrintQueue(setQueueState);
  }, []);

  useEffect(() => {
    if (!queueState.pendingCount) {
      setModalVisible(false);
    }
  }, [queueState.pendingCount]);

  const statusText = useMemo(() => {
    if (queueState.pendingCount <= 1) {
      return queueState.jobs[0]?.label || t('print_queue_printing');
    }

    return t('print_queue_jobs_count', { count: queueState.pendingCount });
  }, [queueState.jobs, queueState.pendingCount, t]);

  if (!queueState.pendingCount) {
    return null;
  }

  return (
    <>
      <TouchableOpacity
        style={styles.badge}
        onPress={() => setModalVisible(true)}
        activeOpacity={0.85}
        accessibilityRole="button"
        accessibilityLabel={t('print_queue_open')}
      >
        <Icon name="printer" size={14} color="#fff" />
        <Text style={styles.text}>{statusText}</Text>
        <View style={styles.countPill}>
          <Text style={styles.countPillText}>{queueState.pendingCount}</Text>
        </View>
      </TouchableOpacity>

      <Modal
        transparent
        animationType="fade"
        visible={modalVisible}
        onRequestClose={() => setModalVisible(false)}
      >
        <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={() => setModalVisible(false)}>
          <TouchableOpacity activeOpacity={1} style={styles.modalCard}>
            <View style={styles.modalHeader}>
              <View>
                <Text style={styles.modalTitle}>{t('print_queue_title')}</Text>
                <Text style={styles.modalSubtitle}>{t('print_queue_jobs_waiting_or_running', { count: queueState.pendingCount })}</Text>
              </View>
              <TouchableOpacity onPress={() => setModalVisible(false)}>
                <Icon name="x" size={18} color="#1f2937" />
              </TouchableOpacity>
            </View>

            {queueState.jobs.map((job, index) => (
              <View key={job.id} style={[styles.jobRow, index === queueState.jobs.length - 1 && styles.jobRowLast]}>
                <View style={styles.jobIconWrap}>
                  <Icon name={index === 0 ? 'play-circle' : 'clock'} size={16} color="#2563eb" />
                </View>
                <View style={styles.jobContent}>
                  <Text style={styles.jobLabel}>{job.label || t('print_queue_job_default_label')}</Text>
                  <Text style={styles.jobMeta}>
                    {index === 0 ? t('print_queue_job_running') : t('print_queue_job_waiting')} • {new Date(job.queuedAt).toLocaleTimeString()}
                  </Text>
                </View>
              </View>
            ))}
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  badge: {
    position: 'absolute',
    top: 82,
    right: 16,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#2563eb',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 18,
    elevation: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    zIndex: 9997,
    maxWidth: 240,
  },
  text: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 13,
    marginLeft: 6,
    flexShrink: 1,
  },
  countPill: {
    marginLeft: 8,
    minWidth: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
  },
  countPillText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '800',
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.45)',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    paddingTop: 120,
    paddingHorizontal: 16,
  },
  modalCard: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: '#fff',
    borderRadius: 20,
    padding: 18,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.18,
    shadowRadius: 18,
    elevation: 8,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  modalTitle: {
    fontSize: 17,
    fontWeight: '800',
    color: '#111827',
  },
  modalSubtitle: {
    marginTop: 4,
    fontSize: 13,
    color: '#6b7280',
  },
  jobRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#e5e7eb',
  },
  jobRowLast: {
    borderBottomWidth: 0,
    paddingBottom: 2,
  },
  jobIconWrap: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#eff6ff',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  jobContent: {
    flex: 1,
  },
  jobLabel: {
    fontSize: 14,
    fontWeight: '700',
    color: '#111827',
  },
  jobMeta: {
    marginTop: 2,
    fontSize: 12,
    color: '#6b7280',
  },
});

export default memo(PrintQueueIndicator);


